// Blueprint editor page (requirements 1.4, 3, 4.1-4.3, 4.5, 5.9).
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Input, { type InputProps } from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Textarea from '@cloudscape-design/components/textarea';
import {
  canonicalJson,
  formToSchema,
  PREVIEW_META,
  PREVIEW_STALE_FIELDS,
  previewSchemaOfFields,
  schemaToForm,
  toPreviewSchema,
  validateFields,
  validateForm,
  validateMetadata,
  type BdaSchema,
  type Blueprint,
  type ValidationErrors,
} from '@bda/shared';
import { api, ApiError } from '../../api/client';
import { keys, useBlueprint, useRun } from '../../api/hooks';
import { SESSION_EXPIRED_EVENT } from '../../auth/auth';
import { hasRole, useAuth } from '../../auth/AuthProvider';
import { SetConflictWarnings } from './BlueprintView';
import { FieldsEditor } from './FieldsEditor';
import { JsonPreview } from '../../components/JsonPreview';
import { PreviewPanel, STARTING_TEXT, usePreview } from '../preview/PreviewPanel';
import { StartFromDocumentStep, SuggestModal } from './SuggestModal';
import { runtimeConfig, setsEnabled } from '../../config';
import { jsonErrorLine } from '../preview/previewFlow';
import { ErrorDetail, errorMessage, useNotify } from '../../lib/notify';
import {
  clearDraft,
  currentSchema,
  initialState,
  loadDraft,
  reducer,
  saveDraft,
  type EditorState,
} from './editorState';

interface Initial {
  state: EditorState;
  revision: number | null;
  restored: boolean;
}

/**
 * A draft mirrored before a forced sign-in wins over the server copy (design 3.3). The test document comes from the
 * draft, else from `?testRun=` (Req 4.4, 7.3).
 */
export function initialEditorState(
  id: string | undefined,
  blueprint: Blueprint | undefined,
  testRun: string | null = null,
): Initial {
  const draft = loadDraft(id);
  if (draft)
    return {
      state: { ...draft.state, dirty: true, testRunId: draft.state.testRunId ?? testRun },
      revision: draft.revision ?? blueprint?.revision ?? null,
      restored: true,
    };
  if (!blueprint) return { state: initialState(undefined, testRun), revision: null, restored: false };
  const res = schemaToForm(blueprint.workingSchema, blueprint.name);
  const meta = { name: blueprint.name, documentType: blueprint.documentType, description: blueprint.description };
  if (res.form)
    return { state: initialState({ ...res.form, ...meta }, testRun), revision: blueprint.revision, restored: false };
  return {
    state: {
      ...initialState({ ...meta, fields: [] }, testRun),
      mode: 'json',
      jsonText: JSON.stringify(blueprint.workingSchema, null, 2),
    },
    revision: blueprint.revision,
    restored: false,
  };
}

export const TEST_DOC_EXPIRED_TEXT = 'The test document has expired. Upload it again to preview.';

const testPages = (n: number | null): string =>
  n === null ? 'Page count unknown' : `${n} ${n === 1 ? 'page' : 'pages'}`;

/**
 * What "Preview with current fields" would request from the current Editor state (Req 4.5): the form's fields, or the
 * parsed JSON text, with placeholder metadata. `basisKey` is the canonical JSON of that schema, so any change to a field
 * or to the JSON text marks a shown preview out of date, and metadata edits or switching views do not (Req 4.7).
 */
export function editorPreviewBasis(
  state: EditorState,
): { schema: BdaSchema; basisKey: string } | { parseError: string } {
  if (state.mode === 'form') {
    const schema = previewSchemaOfFields(state.form.fields);
    return { schema, basisKey: canonicalJson(schema) };
  }
  try {
    const schema = toPreviewSchema(JSON.parse(state.jsonText) as BdaSchema);
    return { schema, basisKey: canonicalJson(schema) };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    const line = jsonErrorLine(state.jsonText, e);
    // V8's "Unexpected token" messages carry no position; show the message alone.
    return { parseError: line === null ? message : `Line ${line}: ${message}` };
  }
}

export function EditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const existing = useBlueprint(id);
  if (id && existing.error)
    return (
      <Alert type="error">
        <ErrorDetail error={existing.error} />
      </Alert>
    );
  if (id && !existing.data) return <Spinner size="large" />;
  return (
    <EditorBody
      key={id ?? 'new'}
      id={id}
      initial={initialEditorState(id, existing.data?.blueprint, params.get('testRun'))}
      name={existing.data?.blueprint.name}
      sets={existing.data?.sets}
    />
  );
}

type BlueprintSets = NonNullable<Awaited<ReturnType<typeof api.getBlueprint>>['sets']>;

/** `sets`: the blueprint detail's set memberships, refetched after each save for the conflict warning (Req 2.7). */
function EditorBody({
  id,
  initial,
  name,
  sets,
}: {
  id: string | undefined;
  initial: Initial;
  name: string | undefined;
  sets?: BlueprintSets;
}) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const notify = useNotify();
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initial.state);
  const testRunId = state.testRunId ?? undefined;
  const testRun = useRun(testRunId);
  // Keep ?testRun= in sync with the state, so a reload or the post-save navigation keeps the test document (Req 4.8).
  const urlTestRun = params.get('testRun');
  useEffect(() => {
    if (state.testRunId && state.testRunId !== urlTestRun) {
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.set('testRun', state.testRunId!);
          return next;
        },
        { replace: true },
      );
    }
  }, [state.testRunId, urlTestRun, setParams]);
  const [revision, setRevision] = useState<number | null>(initial.revision);
  const [saving, setSaving] = useState(false);
  const [bdaError, setBdaError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<number | null>(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  // After a save, a Document_Type that now duplicates another Member of a set is a warning, not an error (Req 2.7).
  const [savedOnce, setSavedOnce] = useState(false);
  // New blueprint: "Start from a document" is the first step (requirement 3.7.1). Editing an existing blueprint, or
  // returning to a restored draft, goes straight to the form.
  const [step, setStep] = useState<'document' | 'editor'>(id || initial.restored ? 'editor' : 'document');
  const nameRef = useRef<InputProps.Ref>(null);
  const focusNameOnEditor = useRef(false);
  useEffect(() => {
    if (step === 'editor' && focusNameOnEditor.current) {
      focusNameOnEditor.current = false;
      nameRef.current?.focus();
    }
  }, [step]);
  const leaveFirstStep = () => {
    focusNameOnEditor.current = true;
    setStep('editor');
  };

  useEffect(() => {
    if (initial.restored) notify.info('Restored your unsaved changes.');
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror to sessionStorage, debounced (design 3.3).
  useEffect(() => {
    if (!state.dirty) return;
    const t = window.setTimeout(() => saveDraft(id, state, revision), 500);
    return () => window.clearTimeout(t);
  }, [state, id, revision]);

  // Session could not be refreshed (requirement 1.4, design 7): write the draft now, without waiting for the
  // debounce, and let the page leave for sign-in without the unsaved-changes prompt. The draft is restored when
  // sign-in returns to this route.
  const leavingForSignIn = useRef(false);
  useEffect(() => {
    const onExpired = () => {
      if (state.dirty) saveDraft(id, state, revision);
      leavingForSignIn.current = true;
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [state, id, revision]);

  // Unsaved-changes guard (requirement 3.8).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => state.dirty && !saving && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (state.dirty && !leavingForSignIn.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [state.dirty]);

  const errors: ValidationErrors = useMemo(
    () => (state.mode === 'form' ? validateForm(state.form) : validateMetadata(state.form)),
    [state.form, state.mode],
  );
  const preview = useMemo(() => (state.mode === 'form' ? formToSchema(state.form) : null), [state.form, state.mode]);
  // ---------- "Preview with current fields" (Req 4.4-4.10) ----------
  const isAuthor = hasRole(user, 'BlueprintAuthor');
  const testDocExpired = testRun.data?.expired === true;
  // A new, unsaved blueprint previews; a saved one keeps "Save and re-run on draft" (Req 4.9).
  const canPreview = !id && runtimeConfig().preview.editorPreview && isAuthor;
  const basis = useMemo(() => editorPreviewBasis(state), [state]);
  const basisKey = 'basisKey' in basis ? basis.basisKey : state.jsonText;
  const previewer = usePreview({ sourceRunId: state.testRunId, enabled: canPreview, basisKey });
  // A different test document (suggestions accepted from another sample) drops the earlier preview.
  const clearPreview = previewer.clear;
  useEffect(() => clearPreview(), [state.testRunId, clearPreview]);
  // Field errors of the last "Preview with current fields" click, recomputed live so fixing a field clears its error.
  const [showPreviewErrors, setShowPreviewErrors] = useState(false);
  const previewFieldErrors: ValidationErrors = useMemo(
    () => (showPreviewErrors && state.mode === 'form' ? validateFields(state.form.fields, PREVIEW_META) : {}),
    [showPreviewErrors, state.mode, state.form.fields],
  );
  // A JSON parse error is shown only while the text it was found in is unchanged.
  const [previewJsonError, setPreviewJsonError] = useState<{ text: string; message: string } | null>(null);
  const jsonErrorText =
    (previewJsonError && previewJsonError.text === state.jsonText ? previewJsonError.message : null) ?? state.jsonError;
  // Server validation errors of the current attempt, keyed by field path like the form's own (Req 4.10, 5.2).
  const attempt = previewer.state.attempt;
  const serverFieldErrors =
    attempt?.phase === 'rejected' && attempt.error.kind === 'validation' ? attempt.error.fieldErrors : {};

  function previewCurrentFields() {
    if (testDocExpired) return;
    if (state.mode === 'form') {
      setShowPreviewErrors(true);
      if (Object.keys(validateFields(state.form.fields, PREVIEW_META)).length > 0) return;
    }
    if ('parseError' in basis) {
      setPreviewJsonError({ text: state.jsonText, message: basis.parseError });
      return;
    }
    setPreviewJsonError(null);
    previewer.request(basis.schema, basis.basisKey);
  }

  const visibleErrors: ValidationErrors = {
    ...serverFieldErrors,
    ...previewFieldErrors,
    ...(showErrors ? errors : {}),
  };

  async function save(overwriteRevision?: number): Promise<string | null> {
    setBdaError(null);
    setShowErrors(true);
    if (Object.keys(errors).length > 0) {
      notify.error('Fix the highlighted fields before saving.');
      return null;
    }
    const s = currentSchema(state);
    if ('error' in s) {
      notify.error(s.error);
      return null;
    }
    const meta = { name: state.form.name, documentType: state.form.documentType, description: state.form.description };
    setSaving(true);
    try {
      const body = { ...meta, schema: s.schema as BdaSchema };
      const saved = id
        ? await api.updateBlueprint(id, { ...body, expectedRevision: overwriteRevision ?? revision ?? 1 })
        : await api.createBlueprint(body);
      clearDraft(id);
      dispatch({ type: 'saved' });
      setRevision(saved.revision);
      setConflict(null);
      await qc.invalidateQueries({ queryKey: keys.blueprint(saved.id) });
      await qc.invalidateQueries({ queryKey: ['blueprints'] });
      setSavedOnce(true);
      notify.success(`Saved "${saved.name}".`);
      if (!id) navigate(`/blueprints/${saved.id}/edit${testRunId ? `?testRun=${testRunId}` : ''}`, { replace: true });
      return saved.id;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'BDA_REJECTED') setBdaError(e.message);
      else if (e instanceof ApiError && e.code === 'CONFLICT')
        setConflict(Number(e.details?.currentRevision ?? 0) || null);
      else if (e instanceof ApiError && e.code === 'VALIDATION_FAILED') notify.error(e.message);
      else notify.error(errorMessage(e), e);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!id) return;
    try {
      const r = await api.publish(id, changeNote);
      setPublishOpen(false);
      setChangeNote('');
      await qc.invalidateQueries({ queryKey: keys.blueprint(id) });
      notify.success(`Published version ${r.version}.`);
    } catch (e) {
      notify.error(
        e instanceof ApiError && e.code === 'BDA_REJECTED' ? `BDA rejected the publish: ${e.message}` : errorMessage(e),
        e,
        'Publish',
      );
    }
  }

  async function saveAndRerun() {
    const savedId = await save();
    if (!savedId || !testRunId) return;
    try {
      const run = await api.retryRun(testRunId, { version: 'DRAFT', blueprintId: savedId });
      navigate(`/runs/${run.id}`);
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  }

  if (step === 'document') {
    return (
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Start from a sample document and BDA suggests the fields, or start with a blank blueprint."
          >
            New blueprint
          </Header>
        }
      >
        <StartFromDocumentStep
          onAccept={(fields, sampleRunId) => {
            dispatch({ type: 'acceptSuggestions', fields, sampleRunId });
            notify.success(
              `Added ${fields.length} suggested ${fields.length === 1 ? 'field' : 'fields'}. Name the blueprint, then save.`,
            );
            leaveFirstStep();
          }}
          onBlank={leaveFirstStep}
          initialRunId={params.get('sampleRun') ?? undefined}
        />
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={
            id
              ? 'Changes are saved to the draft. Publish to create a fixed version.'
              : 'Define the fields BDA should extract.'
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowSuggest(true)}>Start from a document</Button>
              {id && (
                <Button disabled={state.dirty} onClick={() => setPublishOpen(true)}>
                  Publish
                </Button>
              )}
              <Button variant="primary" loading={saving} onClick={() => void save()}>
                Save
              </Button>
            </SpaceBetween>
          }
        >
          {id ? `Edit ${name ?? ''}` : 'New blueprint'}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {savedOnce && setsEnabled() && isAuthor && <SetConflictWarnings sets={sets} />}
        {bdaError && (
          <Alert type="error" header="BDA rejected this blueprint" dismissible onDismiss={() => setBdaError(null)}>
            {bdaError} Your edits are kept.
          </Alert>
        )}
        {conflict !== null && (
          <Alert
            type="warning"
            header="This blueprint changed in another tab"
            action={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => window.location.reload()}>Reload</Button>
                <Button onClick={() => void save(conflict)}>Overwrite</Button>
              </SpaceBetween>
            }
          >
            Reload to see the latest version, or overwrite it with your changes.
          </Alert>
        )}
        {testRunId && testRun.data && (
          <Alert
            type={testDocExpired ? 'warning' : 'info'}
            header={`Test document: ${testRun.data.fileName}`}
            action={
              canPreview ? (
                <Button
                  disabled={testDocExpired || previewer.status.busy}
                  disabledReason={
                    testDocExpired ? TEST_DOC_EXPIRED_TEXT : previewer.status.busy ? STARTING_TEXT : undefined
                  }
                  onClick={previewCurrentFields}
                >
                  Preview with current fields
                </Button>
              ) : (
                <Button onClick={() => void saveAndRerun()}>Save and re-run on draft</Button>
              )
            }
          >
            <SpaceBetween size="xxs">
              <Box>{testPages(testRun.data.sourcePages ?? testRun.data.pages)}</Box>
              <Box>
                {testDocExpired
                  ? TEST_DOC_EXPIRED_TEXT
                  : canPreview
                    ? 'Preview what BDA extracts from this document with the current fields. The blueprint is not saved.'
                    : 'Edit fields, then re-run this document against the draft blueprint.'}
              </Box>
            </SpaceBetween>
          </Alert>
        )}
        {canPreview && testRunId && (
          <PreviewPanel preview={previewer} basisKey={basisKey} staleText={PREVIEW_STALE_FIELDS} idPrefix="editor" />
        )}
        <Form>
          <SpaceBetween size="l">
            <Container header={<Header variant="h2">Details</Header>}>
              <SpaceBetween size="m">
                <FormField
                  label="Name"
                  constraintText="1-128 characters: letters, digits, spaces, hyphens, underscores."
                  errorText={visibleErrors.name}
                >
                  <Input
                    ref={nameRef}
                    value={state.form.name}
                    onChange={(e) => dispatch({ type: 'meta', field: 'name', value: e.detail.value })}
                  />
                </FormField>
                <FormField
                  label="Document type"
                  description="For example: Invoice, Bridge inspection report."
                  errorText={visibleErrors.documentType}
                >
                  <Input
                    value={state.form.documentType}
                    onChange={(e) => dispatch({ type: 'meta', field: 'documentType', value: e.detail.value })}
                  />
                </FormField>
                <FormField
                  label="Description"
                  constraintText={`${state.form.description.length}/500`}
                  errorText={visibleErrors.description}
                >
                  <Textarea
                    value={state.form.description}
                    rows={2}
                    onChange={(e) => dispatch({ type: 'meta', field: 'description', value: e.detail.value })}
                  />
                </FormField>
              </SpaceBetween>
            </Container>

            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <SegmentedControl
                      label="Editing mode"
                      selectedId={state.mode}
                      options={[
                        { id: 'form', text: 'Form' },
                        { id: 'json', text: 'JSON' },
                      ]}
                      onChange={(e) => dispatch({ type: e.detail.selectedId === 'json' ? 'toJson' : 'toForm' })}
                    />
                  }
                >
                  Fields
                </Header>
              }
            >
              {state.mode === 'form' ? (
                <Grid gridDefinition={[{ colspan: { default: 12, l: 7 } }, { colspan: { default: 12, l: 5 } }]}>
                  <FieldsEditor fields={state.form.fields} errors={visibleErrors} dispatch={dispatch} />
                  <SpaceBetween size="xs">
                    <Box variant="h3">Schema preview</Box>
                    <JsonPreview value={preview} />
                  </SpaceBetween>
                </Grid>
              ) : (
                <SpaceBetween size="s">
                  {state.unsupported.length > 0 && (
                    <Alert type="warning" header="The form can't show this schema">
                      Stay in JSON mode, or remove these parts to switch back: {state.unsupported.join(', ')}
                    </Alert>
                  )}
                  <FormField label="Blueprint schema JSON" errorText={jsonErrorText}>
                    <Textarea
                      value={state.jsonText}
                      rows={28}
                      spellcheck={false}
                      onChange={(e) => dispatch({ type: 'jsonText', text: e.detail.value })}
                    />
                  </FormField>
                </SpaceBetween>
              )}
            </Container>
          </SpaceBetween>
        </Form>
      </SpaceBetween>

      <SuggestModal
        visible={showSuggest}
        onDismiss={() => setShowSuggest(false)}
        editorFields={state.form.fields}
        onAccept={(fields, sampleRunId) => dispatch({ type: 'acceptSuggestions', fields, sampleRunId })}
      />

      <Modal
        visible={publishOpen}
        onDismiss={() => setPublishOpen(false)}
        header="Publish a version"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setPublishOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={changeNote.length > 500} onClick={() => void publish()}>
                Publish
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label="Change note (optional)"
          constraintText={`${changeNote.length}/500`}
          errorText={changeNote.length > 500 ? 'Keep the note to 500 characters.' : undefined}
        >
          <Textarea value={changeNote} onChange={(e) => setChangeNote(e.detail.value)} />
        </FormField>
      </Modal>

      <Modal
        visible={blocker.state === 'blocked'}
        onDismiss={() => blocker.reset?.()}
        header="Leave without saving?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => blocker.reset?.()}>
                Stay
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  clearDraft(id);
                  blocker.proceed?.();
                }}
              >
                Leave
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        You have unsaved changes to this blueprint.
      </Modal>
    </ContentLayout>
  );
}
